import { useState } from "react";
import { AudioLines, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Section, Param } from "@/components/editor/Section";
import { AssetList } from "@/components/editor/AssetList";
import { api, formatApiError } from "@/lib/api";

const LIBRARY = [
  ["Door knock", "Three firm knocks on a heavy wooden front door, close-mic'd"],
  ["Footsteps · wood", "Slow footsteps of leather shoes walking across creaky wooden floorboards"],
  ["Footsteps · gravel", "Boots crunching on gravel path, steady walking pace"],
  ["Door creak", "Old iron-hinged door slowly creaking open in a quiet hallway"],
  ["Thunder", "Distant rolling thunder followed by a sharp close lightning crack"],
  ["Rain on window", "Gentle rain pattering on a glass window, interior perspective"],
  ["Car pass-by", "Car passing by at speed on a wet road, left to right"],
  ["Glass shatter", "Wine glass falling and shattering on a stone floor"],
  ["Crowd murmur", "Indoor restaurant crowd murmur with cutlery clinks"],
  ["Heartbeat", "Slow tense heartbeat, deep and muffled, cinematic"],
  ["Sword draw", "Steel sword unsheathed with a bright metallic ring"],
  ["Phone ring", "Old rotary telephone ringing in an empty room"],
];

export const SfxPanel = ({ projectId, assets, onCreated, onAdd }) => {
  const [prompt, setPrompt] = useState("");
  const [duration, setDuration] = useState(4);
  const [auto, setAuto] = useState(false);
  const [influence, setInfluence] = useState(0.4);
  const [loop, setLoop] = useState(false);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/generate/sfx", { project_id: projectId, prompt, duration: auto ? null : duration, prompt_influence: influence, loop });
      onCreated(data); toast.success("Sound effect is being generated");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="sfx-panel">
      <Section title="Foley & sound effects">
        <div className="flex flex-wrap gap-1.5 mb-3">
          {LIBRARY.map(([l, p]) => (
            <button key={l} onClick={() => setPrompt(p)} data-testid={`sfx-chip-${l.replace(/\W+/g, "-").toLowerCase()}`}
              className="text-[11px] px-2 py-1 rounded-full border border-[#2A2E3D] hover:border-[#8B5CF6] hover:text-[#c4b5fd] transition-colors">{l}</button>
          ))}
        </div>
        <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Describe exactly the sound you need…" className="bg-[#0B0C10] text-sm min-h-[72px]" data-testid="sfx-prompt-input" />
        <div className="flex items-center justify-between mt-3 text-xs text-slate-300">
          <span>Auto length</span><Switch checked={auto} onCheckedChange={setAuto} data-testid="sfx-auto-switch" />
        </div>
        {!auto && <Param label="Length (s)" value={duration} min={0.5} max={22} step={0.5} onChange={setDuration} testId="sfx-duration" />}
        <Param label="Prompt adherence" value={influence} min={0} max={1} onChange={setInfluence} testId="sfx-influence" />
        <div className="flex items-center justify-between mt-1 text-xs text-slate-300"><span>Seamless loop</span><Switch checked={loop} onCheckedChange={setLoop} data-testid="sfx-loop-switch" /></div>
        <Button onClick={generate} disabled={busy || prompt.trim().length < 2} className="w-full mt-3 bg-[#8B5CF6] hover:bg-[#9f75ff] text-white" data-testid="sfx-generate-button">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <AudioLines className="w-4 h-4 mr-2" />}Generate sound effect
        </Button>
      </Section>
      <Section title="Your SFX">
        <AssetList assets={assets.filter((a) => a.kind === "sfx")} empty="Generated effects appear here." onAdd={onAdd} />
      </Section>
    </div>
  );
};
